import type { CreativeLibrary, StoredTestReport, TestRecommendation, UaTest, UaTestInput, UaTestReport, UaTestVariant } from '@growth/client';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { AttributionBadge, RECOMMENDATION, ReliabilityBadge, StateBadge } from '../components/ua.tsx';
import { Card, DataTable, Empty, ErrorBox, Icon, StatTile } from '../components/ui.tsx';
import { fmtDateTime, fmtDay, fmtInt, fmtMoney, fmtMoneyRange, fmtNum, fmtPct } from '../format.ts';
import { ciText } from '../metrics.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

const STATUS_LABEL: Record<UaTest['status'], string> = { draft: 'Borrador', running: 'En marcha', completed: 'Terminado', cancelled: 'Cancelado' };

type VariantKind = 'creative_id' | 'concept_id' | 'campaign_id' | 'ad_group_id';
interface VariantDraft {
  variant_key: string;
  label: string;
  kind: VariantKind;
  id: string;
  is_control: boolean;
}

const KIND_LABEL: Record<VariantKind, string> = { creative_id: 'Creatividad', concept_id: 'Concepto', campaign_id: 'Campaña', ad_group_id: 'Grupo de anuncios' };

function NewTestForm({ gameId, onCreated }: { gameId: string; onCreated: (n: number) => void }) {
  const { api } = useSession();
  const action = useAction();
  const creatives = useApi<CreativeLibrary>(`creatives:${gameId}:all`, (a, s) => a.creatives(gameId, { last: '30d' }, s));
  const [form, setForm] = useState({ name: '', hypothesis: '', budget: '100', target_cpi: '', start_date: '', end_date: '', countries: '', mode: 'small_budget', status: 'draft' });
  const [variants, setVariants] = useState<VariantDraft[]>([
    { variant_key: 'control', label: 'Control', kind: 'creative_id', id: '', is_control: true },
    { variant_key: 'b', label: 'Variante B', kind: 'creative_id', id: '', is_control: false },
  ]);
  const setV = (i: number, patch: Partial<VariantDraft>) => setVariants(variants.map((v, k) => (k === i ? { ...v, ...patch } : patch.is_control ? { ...v, is_control: false } : v)));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const input: UaTestInput = {
      name: form.name.trim(),
      hypothesis: form.hypothesis || null,
      budget: form.budget ? Number(form.budget) : null,
      target_cpi: form.target_cpi ? Number(form.target_cpi) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      countries: form.countries ? form.countries.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean) : [],
      mode: form.mode as UaTest['mode'],
      status: form.status as UaTest['status'],
      variants: variants.map((v) => ({ variant_key: v.variant_key.trim(), label: v.label || null, [v.kind]: v.id.trim(), is_control: v.is_control })),
    };
    const r = await action.run(() => api.createUaTest(gameId, input));
    if (r) {
      invalidateCache(`ua-tests:${gameId}`);
      onCreated(r.test.number);
    }
  };
  return (
    <Card title="NEW UA TEST" subtitle="Plataforma Android, red Google Ads, objetivo instalaciones. La plataforma no crea ni cambia campañas: configura el test en Google Ads y apunta aquí qué compara.">
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span>Nombre</span>
            <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Test de 100 € — ganchos" />
          </label>
          <label className="field">
            <span>Presupuesto ({'€'})</span>
            <input className="input" type="number" min={0} step="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
          </label>
          <label className="field">
            <span>CPI objetivo</span>
            <input className="input" type="number" min={0} step="0.01" value={form.target_cpi} onChange={(e) => setForm({ ...form, target_cpi: e.target.value })} placeholder="del juego si se deja vacío" />
          </label>
          <label className="field">
            <span>Inicio</span>
            <input className="input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </label>
          <label className="field">
            <span>Fin</span>
            <input className="input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </label>
          <label className="field">
            <span>Países (ISO, coma)</span>
            <input className="input" value={form.countries} onChange={(e) => setForm({ ...form, countries: e.target.value })} placeholder="ES,MX" />
          </label>
          <label className="field">
            <span>Modo</span>
            <select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="small_budget">Presupuesto pequeño (100 €)</option>
              <option value="standard">Estándar</option>
            </select>
          </label>
          <label className="field">
            <span>Estado</span>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            <span>Hipótesis</span>
            <input className="input" value={form.hypothesis} onChange={(e) => setForm({ ...form, hypothesis: e.target.value })} placeholder="El gancho de hordas consigue un CPI menor que el fail bait sin peor D1" />
          </label>
        </div>
        <datalist id="creative-ids">
          {(creatives.data?.creatives ?? []).map((c) => (
            <option key={c.creative_id} value={c.creative_id}>
              {c.name}
            </option>
          ))}
        </datalist>
        <div className="stack">
          <span className="muted small">Variantes (marca una como control para comparar; sin control se compara con el CPI objetivo)</span>
          {variants.map((v, i) => (
            <div key={i} className="variant-editor">
              <label className="field">
                <span>Clave</span>
                <input className="input" required value={v.variant_key} onChange={(e) => setV(i, { variant_key: e.target.value })} />
              </label>
              <label className="field">
                <span>Qué es</span>
                <select className="input" value={v.kind} onChange={(e) => setV(i, { kind: e.target.value as VariantKind })}>
                  {Object.entries(KIND_LABEL).map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Id</span>
                <input className="input" required value={v.id} list={v.kind === 'creative_id' ? 'creative-ids' : undefined} onChange={(e) => setV(i, { id: e.target.value })} />
              </label>
              <label className="field">
                <span>Etiqueta</span>
                <input className="input" value={v.label} onChange={(e) => setV(i, { label: e.target.value })} />
              </label>
              <label className="check">
                <input type="radio" name="control" checked={v.is_control} onChange={() => setV(i, { is_control: true })} />
                Control
              </label>
              <button type="button" className="btn small ghost" aria-label="Quitar variante" disabled={variants.length <= 1} onClick={() => setVariants(variants.filter((_, k) => k !== i))}>
                ✕
              </button>
            </div>
          ))}
          <div className="row">
            <button type="button" className="btn small" onClick={() => setVariants([...variants, { variant_key: String.fromCharCode(97 + variants.length), label: '', kind: 'creative_id', id: '', is_control: false }])}>
              Añadir variante
            </button>
          </div>
        </div>
        {action.error && <ErrorBox error={action.error} />}
        <div className="row">
          <button type="submit" className="btn primary" disabled={action.busy}>
            Crear test
          </button>
        </div>
      </form>
    </Card>
  );
}

export function TestLabPage() {
  const { gameId = '' } = useParams();
  const { isAdmin } = useSession();
  const navigate = useNavigate();
  const tests = useApi<UaTest[]>(`ua-tests:${gameId}`, (api) => api.uaTests(gameId));
  const [creating, setCreating] = useState(false);
  const list = tests.data ?? [];
  return (
    <>
      <PageHeader
        title="Test Lab"
        sub="Tests de UA con presupuesto pequeño: señales tempranas frente a resultados fiables, y una recomendación STOP / ITERATE / TEST AGAIN / SCALE que siempre decide una persona."
        right={
          isAdmin && (
            <button type="button" className="btn primary" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Cerrar' : 'Nuevo test'}
            </button>
          )
        }
      />
      {tests.error && <ErrorBox error={tests.error} onRetry={tests.reload} />}
      {creating && isAdmin && <NewTestForm gameId={gameId} onCreated={(n) => navigate(`/g/${gameId}/tests/${n}`)} />}
      <Card title="Tests">
        {tests.data && !list.length ? (
          <Empty>Ningún test todavía. El primero suele ser el de 100 €: ¿cuánto cuesta una instalación y vuelven al día siguiente?</Empty>
        ) : (
          <DataTable
            onRowClick={(i) => navigate(`/g/${gameId}/tests/${list[i]!.number}`)}
            data={{
              columns: [{ label: 'Test' }, { label: 'Nombre' }, { label: 'Estado' }, { label: 'Fechas' }, { label: 'Presupuesto', numeric: true }, { label: 'Variantes', numeric: true }, { label: 'Recomendación' }, { label: 'Decisión' }],
              rows: list.map((t) => [
                `TEST #${t.number}`,
                t.name,
                STATUS_LABEL[t.status],
                `${t.start_date ? fmtDay(t.start_date) : '—'} – ${t.end_date ? fmtDay(t.end_date) : '—'}`,
                fmtMoney(t.budget, t.currency, 0),
                fmtInt(t.variants),
                t.last_recommendation && t.last_recommendation !== 'none' ? (RECOMMENDATION[t.last_recommendation as TestRecommendation]?.label ?? t.last_recommendation) : '—',
                t.decision ? RECOMMENDATION[t.decision].label : '—',
              ]),
            }}
          />
        )}
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ detail

function VariantsTable({ report }: { report: UaTestReport }) {
  const c = report.currency;
  return (
    <DataTable
      data={{
        columns: [
          { label: 'Variante' },
          { label: 'Estado' },
          { label: 'Instalaciones', numeric: true },
          { label: 'Gasto', numeric: true },
          { label: 'CTR', numeric: true },
          { label: 'IPM (IC 95 %)', numeric: true },
          { label: 'CPI (IC 95 %)', numeric: true },
          { label: 'Tutorial', numeric: true },
          { label: 'D1', numeric: true },
          { label: 'Fiabilidad' },
        ],
        rows: report.variants.map((v) => [
          <span title={JSON.stringify(v.selector)}>
            {v.label ?? v.variant_key}
            {v.is_control ? ' (control)' : ''}
            {v.network && (
              <>
                {' '}
                <AttributionBadge kind={v.network.attribution} />
              </>
            )}
          </span>,
          <StateBadge state={v.state} control={v.is_control} />,
          fmtNum(v.network?.installs ?? v.game?.installs, 1),
          fmtMoney(v.network?.spend, c),
          fmtPct(v.network?.ctr.value, 2),
          v.network?.ipm.value !== null && v.network ? `${fmtNum(v.network.ipm.value, 2)} (${fmtNum(v.network.ipm.low, 2)}–${fmtNum(v.network.ipm.high, 2)})` : '—',
          v.network?.cpi.value !== null && v.network ? `${fmtMoney(v.network.cpi.value, c, 3)} (${fmtMoneyRange(v.network.cpi.low, v.network.cpi.high, c)})` : '—',
          v.game ? `${fmtPct(v.game.tutorial_complete.rate)} n=${v.game.tutorial_complete.n}` : '—',
          v.game?.d1.n ? `${fmtPct(v.game.d1.rate)} n=${v.game.d1.n}` : '—',
          <span className="row" style={{ gap: 4 }}>
            <ReliabilityBadge value={v.reliability.cpi ?? 'insufficient'} prefix="CPI" />
            <ReliabilityBadge value={v.reliability.d1 ?? 'insufficient'} prefix="D1" />
          </span>,
        ]),
      }}
    />
  );
}

const DIAG_AREA: Record<string, string> = { ad: 'Anuncio', store_listing: 'Ficha de la tienda', onboarding: 'Onboarding' };

export function TestDetailPage() {
  const { gameId = '', testId = '' } = useParams();
  const { api, isAdmin } = useSession();
  const meta = useApi<{ test: UaTest; variants: UaTestVariant[]; reports: { id: number; generated_at: string; recommendation: string }[] }>(`ua-test:${gameId}:${testId}`, (a) => a.uaTest(gameId, testId));
  const ev = useApi<UaTestReport>(`ua-test-ev:${gameId}:${testId}`, (a, s) => a.uaTestEvaluation(gameId, testId, s));
  const action = useAction();
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState<StoredTestReport | null>(null);
  const r = ev.data;
  const t = meta.data?.test;
  const c = r?.currency ?? 'EUR';
  const reload = () => {
    invalidateCache(`ua-test:${gameId}:${testId}`);
    invalidateCache(`ua-test-ev:${gameId}:${testId}`);
    invalidateCache(`ua-tests:${gameId}`);
    meta.reload();
    ev.reload();
  };
  const decide = async (d: TestRecommendation) => {
    if (d === 'scale' && !window.confirm('Registrar SCALE? Game Growth OS no cambia presupuestos: tendrás que subirlo tú en Google Ads.')) return;
    if (await action.run(() => api.decideUaTest(gameId, testId, d, note || undefined))) reload();
  };

  return (
    <>
      <PageHeader
        title={t ? `TEST #${t.number} · ${t.name}` : `TEST #${testId}`}
        sub={t && `${STATUS_LABEL[t.status]} · ${t.mode === 'small_budget' ? 'presupuesto pequeño' : 'estándar'} · ${t.network} · ${t.countries.length ? t.countries.join(', ') : 'todos los países'}${t.hypothesis ? ` · ${t.hypothesis}` : ''}`}
        right={
          <Link className="btn small" to={`/g/${gameId}/tests`}>
            ← Tests
          </Link>
        }
      />
      {(meta.error || ev.error) && <ErrorBox error={(meta.error ?? ev.error)!} onRetry={reload} />}
      {r && (
        <>
          <div className={`kpis loading-frame${ev.loading ? ' stale' : ''}`}>
            <StatTile label="Gasto" value={fmtMoney(r.spend.total, c)} sub={r.spend.budget ? `${fmtPct(r.spend.budget_used, 0)} de ${fmtMoney(r.spend.budget, c, 0)}` : 'sin presupuesto fijado'} />
            <StatTile label="Ventana" value={`${r.window.days} días`} sub={`${fmtDay(r.window.from)} – ${fmtDay(r.window.to)}${r.window.ended ? ' · terminado' : ''}`} />
            <StatTile label="Instalaciones (red)" value={fmtNum(r.totals.installs, 0)} sub={`CPI ${fmtMoney(r.totals.cpi, c, 3)}${r.test.target_cpi ? ` · objetivo ${fmtMoney(r.test.target_cpi, c, 2)}` : ''}`} />
            <StatTile label="CTR" value={fmtPct(r.totals.ctr.rate, 2)} sub={ciText(r.totals.ctr)} />
            <StatTile label="Tutorial completado" value={fmtPct(r.totals.tutorial_complete.rate)} sub={ciText(r.totals.tutorial_complete)} />
            <StatTile label="Retención D1" value={fmtPct(r.totals.d1.rate)} sub={ciText(r.totals.d1)} />
          </div>

          <div className="reco" role="status" style={{ marginBottom: 16 }}>
            <div className="small muted">Recomendación automática</div>
            <div className="reco-title">{r.recommendation ? RECOMMENDATION[r.recommendation].label : 'Aún sin recomendación'}</div>
            {r.recommendation && <div className="small">{RECOMMENDATION[r.recommendation].help}</div>}
            <ul>
              {r.recommendation_reasons.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {r.decision && (
              <div style={{ marginTop: 8 }}>
                <strong>Decisión registrada:</strong> {RECOMMENDATION[r.decision.decision].label} · {fmtDateTime(r.decision.decided_at)}
                {r.decision.note ? ` · ${r.decision.note}` : ''}
              </div>
            )}
          </div>

          <Card title="Variantes" subtitle="Una diferencia solo cuenta si los intervalos de confianza no se solapan. SCALE nunca se recomienda solo por CTR.">
            <VariantsTable report={r} />
            <ul className="stack small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {r.variants
                .filter((v) => !v.is_control)
                .map((v) => (
                  <li key={v.variant_key}>
                    <strong>{v.label ?? v.variant_key}</strong>: {v.reasons.join(' ')}
                  </li>
                ))}
            </ul>
          </Card>

          {r.variants.some((v) => v.revenue || v.network_value) && (
            <Card title="Ingresos por variante" subtitle="LTV D7 de sus jugadores (atribución directa, neto estimado) y valor de conversión que Google Ads atribuye a la campaña. No cambian los estados del test: informan la decisión.">
              <DataTable
                compact
                data={{
                  columns: [
                    { label: 'Variante' },
                    { label: 'Jugadores con 7 días', numeric: true },
                    { label: 'LTV D7', numeric: true },
                    { label: 'LTV D7 / CPI', numeric: true },
                    { label: 'Valor Google Ads', numeric: true },
                    { label: 'ROAS según la red', numeric: true },
                  ],
                  rows: r.variants.map((v) => [
                    v.label ?? v.variant_key,
                    fmtInt(v.revenue?.mature_installs),
                    fmtMoney(v.revenue?.ltv_d7, r.currency, 3),
                    fmtPct(v.revenue?.ltv_d7_over_cpi, 0),
                    fmtMoney(v.network_value?.value, r.currency),
                    fmtPct(v.network_value?.roas, 0),
                  ]),
                }}
              />
            </Card>
          )}

          {r.variants.some((v) => v.network_actions?.length) && (
            <Card title="Acciones en la app según Google Ads" subtitle="Eventos de Firebase importados como conversiones (atribución compartida): activación por variante aunque el juego no sepa de qué campaña viene cada jugador.">
              <DataTable
                compact
                data={{
                  columns: [{ label: 'Variante' }, { label: 'Acción' }, { label: 'Conversiones', numeric: true }, { label: 'Por instalación', numeric: true }],
                  rows: r.variants.flatMap((v) => (v.network_actions ?? []).map((a) => [v.label ?? v.variant_key, a.action, fmtNum(a.conversions, 1), fmtPct(a.per_install)])),
                }}
              />
            </Card>
          )}

          <div className="grid cols-2">
            <Card title="Las preguntas del test">
              <div className="qa">
                {r.answers.map((a) => (
                  <div key={a.id}>
                    <div className="q">{a.question}</div>
                    <div className="a">
                      {a.answer} <ReliabilityBadge value={a.reliability} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="¿Dónde se pierde la gente?" subtitle="Señales heurísticas, no causas.">
              <div className="qa">
                {r.diagnosis.map((d) => (
                  <div key={d.area}>
                    <div className="q row" style={{ gap: 6 }}>
                      <Icon name={d.status === 'ok' ? 'good' : d.status === 'possible_problem' ? 'warning' : 'info'} />
                      {DIAG_AREA[d.area] ?? d.area}: {d.status === 'ok' ? 'sin señales de problema' : d.status === 'possible_problem' ? 'posible problema' : 'sin datos suficientes'}
                    </div>
                    <div className="a">{d.evidence}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {isAdmin && (
            <Card title="Decisión" subtitle="La toma una persona. Registrarla no cambia nada en Google Ads.">
              <div className="stack">
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional): qué harás y por qué" aria-label="Nota de la decisión" />
                <div className="row">
                  {(Object.keys(RECOMMENDATION) as TestRecommendation[]).map((d) => (
                    <button key={d} type="button" className={`btn${r.recommendation === d ? ' primary' : ''}`} disabled={action.busy} onClick={() => decide(d)} title={RECOMMENDATION[d].help}>
                      {RECOMMENDATION[d].label}
                    </button>
                  ))}
                  <span className="spacer" style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="btn"
                    disabled={action.busy}
                    onClick={async () => {
                      const s = await action.run(() => api.generateUaTestReport(gameId, testId));
                      if (s) {
                        setSaved(s);
                        meta.reload();
                      }
                    }}
                  >
                    Guardar informe
                  </button>
                </div>
                {saved && <div className="notice">Informe #{saved.id} guardado ({fmtDateTime(saved.generated_at)}).</div>}
                {action.error && <ErrorBox error={action.error} />}
              </div>
            </Card>
          )}

          {meta.data && meta.data.reports.length > 0 && (
            <Card title="Informes guardados">
              <DataTable compact data={{ columns: [{ label: 'Informe' }, { label: 'Fecha' }, { label: 'Recomendación' }], rows: meta.data.reports.map((x) => [`#${x.id}`, fmtDateTime(x.generated_at), RECOMMENDATION[x.recommendation as TestRecommendation]?.label ?? x.recommendation]) }} />
            </Card>
          )}

          <Card title="Advertencias">
            <ul className="stack small" style={{ margin: 0, paddingLeft: 18 }}>
              {r.caveats.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </>
  );
}
